import React, { forwardRef, useMemo } from "react";
import AppMessageBoxWrapper from "../UtilsComponents/AppMessageBoxWrapper";
import { StyledMessageText } from "./../../Styles/StyledComponents";
import { processHTMLLinks } from "../../utils/processtext";
import { Box } from "@mui/material";
import TextToSpeechButton from "../Features/AppRenderTextToSpeech";
import { useAppSelector } from "../../../Store";
import { useBotConfigStore } from "../../../Store/Slices/ChatbotSlices/BotConfigSlice";
import { sanitizeHtml } from "../../utils/security/sanitizeHtml";

interface Props {
  isFullScreen?: boolean;
  userMessage: boolean;
  children: any;
  time: string;
  readStatus?: "sent" | "delivered" | "seen";
  isError?: boolean;
  ref?: any;
  [otherProps: string]: any;
}
const AppRenderText: React.FC<Props> = React.memo(
  forwardRef<HTMLDivElement, Props>(
    (
      {
        userMessage = false,
        children,
        time,
        readStatus = "delivered",
        isFullScreen,
        isError = false,
        ...otherProps
      },
      ref
    ) => {
      const [isSpeaking, setIsSpeaking] = React.useState(false);
      const { botConfig } = useAppSelector(useBotConfigStore);

      const isTextToSpeechEnabled = useMemo(() => {
        return botConfig?.config?.voice_settings?.text_to_speech_enabled ?? false;
      }, [botConfig?.config?.voice_settings?.text_to_speech_enabled]);

      const speakText = (htmlText: any) => {
        if ("speechSynthesis" in window) {
          // Use a DOMParser to parse the HTML and extract text content
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlText || "", "text/html");
          const text = doc.body.textContent || "";

          if (text.trim() === "") {
            // No text to speak, return early
            return;
          }

          const utterance = new SpeechSynthesisUtterance(text);

          // Set additional options for the utterance if needed
          // utterance.voice = ...
          // utterance.rate = ...

          // Event handler for when speech ends
          utterance.onend = () => {
            setIsSpeaking(false);
          };

          // Start or stop speaking based on the current state
          if (isSpeaking) {
            window.speechSynthesis.cancel();
          } else {
            window.speechSynthesis.speak(utterance);
          }
          setIsSpeaking(!isSpeaking);
        }
      };
      const processedText = useMemo(() => sanitizeHtml(processHTMLLinks(children) ?? ''), [children]);

      if (!children || children?.length < 1) {
        return null;
      }

      return (
        <AppMessageBoxWrapper
          userMessage={userMessage}
          time={time}
          isMedia={false}
          readStatus={readStatus}
          ref={ref}
          isError={isError}
        >
          {/* Text To Speech */}
          {userMessage ? null : (
            isTextToSpeechEnabled && (
              <Box
                className="speaker"
                sx={{ position: "absolute", right: "-8px", top: "-6px" }}
            >
                <TextToSpeechButton text={children} />
              </Box>
            )
          )}

          <StyledMessageText
            isFullScreen={isFullScreen}
            userMessage={userMessage}
            dangerouslySetInnerHTML={{
              __html: processedText,
            }}
            isError={isError}
            {...otherProps}
          />
        </AppMessageBoxWrapper>
      );
    }
  )
);

export default AppRenderText;
